import { CompanyProfile, Job, JobCardModel } from '@/interfaces/workable';

export const WORKABLE_API_URL =
  'https://apply.workable.com/api/v1/widget/accounts/devsu';

/**
 * Turn Workable's `employment_type` (e.g. `full_time`) into a human label
 * (`Full-time`). Shared across every Workable job card renderer.
 */
export function formatEmploymentType(type: string): string {
  if (!type) {
    return '';
  }
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('-');
}

/**
 * Location string for a Workable job. Remote if `telecommuting` is true;
 * otherwise `city, country` (skipping missing pieces). Card renderers that
 * want just the city can read `job.city` directly.
 */
export function getJobLocation(job: Job): string {
  if (job.telecommuting) {
    return 'Remote';
  }
  const parts = [job.city, job.country].filter(Boolean);
  return parts.join(', ');
}

/**
 * Stable React key / dedupe id for a Workable job.
 *
 * Workable reuses `shortcode` when the same role is posted to multiple
 * locations (e.g. one remote listing in Colombia and another remote listing
 * in Peru), so `shortcode` alone isn't unique. This compound key adds
 * `telecommuting|city|country` so remote variants across countries — and
 * on-site variants across cities — each get their own identity.
 */
export function getJobKey(job: Job): string {
  const remote = job.telecommuting ? 'r' : 'o';
  return `${job.shortcode}::${remote}|${job.city ?? ''}|${job.country ?? ''}`;
}

/**
 * Project a raw Workable `Job` (network model) into the `JobCardModel` used by
 * card renderers (view model). Centralizes location + employment-type
 * formatting so every renderer displays the same thing for the same job.
 */
export function toJobCardModel(job: Job): JobCardModel {
  return {
    key: getJobKey(job),
    title: job.title,
    location: job.telecommuting ? 'Remote' : job.city,
    type: formatEmploymentType(job.employment_type),
    applyUrl: job.application_url,
  };
}

/**
 * Fetches job listings from Workable API
 * @returns Promise with company profile including all job listings
 */
export async function fetchWorkableJobs(): Promise<CompanyProfile> {
  try {
    const response = await fetch(WORKABLE_API_URL, {
      next: { revalidate: 3600 }, // Cache for 1 hour
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch jobs: ${response.statusText}`);
    }

    const data: CompanyProfile = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching Workable jobs:', error);
    throw error;
  }
}

/**
 * Fetches job listings with optional filtering
 * @param filters - Optional filters for department, location, etc.
 * @returns Promise with filtered job listings
 */
export async function getFilteredJobs(filters?: {
  department?: string;
  telecommuting?: boolean;
  country?: string;
  city?: string;
}) {
  const data = await fetchWorkableJobs();

  if (!filters) {
    return data.jobs;
  }

  return data.jobs.filter((job) => {
    if (filters.department && job.department !== filters.department) {
      return false;
    }
    if (
      filters.telecommuting !== undefined &&
      job.telecommuting !== filters.telecommuting
    ) {
      return false;
    }
    if (filters.country && job.country !== filters.country) {
      return false;
    }
    if (filters.city && job.city !== filters.city) {
      return false;
    }
    return true;
  });
}

/**
 * Gets unique departments from job listings
 */
export async function getDepartments(): Promise<string[]> {
  const data = await fetchWorkableJobs();
  const departments = new Set(data.jobs.map((job) => job.department));
  return Array.from(departments).sort();
}

/**
 * Gets unique locations from job listings
 */
export async function getLocations(): Promise<
  Array<{ country: string; city: string }>
> {
  const data = await fetchWorkableJobs();
  const locations = new Set<string>();

  data.jobs.forEach((job) => {
    job.locations.forEach((loc) => {
      if (!loc.hidden) {
        locations.add(JSON.stringify({ country: loc.country, city: loc.city }));
      }
    });
  });

  return Array.from(locations)
    .map((loc) => JSON.parse(loc))
    .sort((a, b) => {
      if (a.country !== b.country) {
        return a.country.localeCompare(b.country);
      }
      return a.city.localeCompare(b.city);
    });
}
